import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Recycle, Truck, Globe, Award, ClipboardCheck, Package, ArrowRight } from 'lucide-react';

interface HomePageProps {
  onPageChange: (page: string) => void;
}

export function HomePage({ onPageChange }: HomePageProps) {
  const services = [
    {
      icon: <Recycle className="w-8 h-8 text-[#fe2b32]" />,
      title: "Ferrous Materials",
      description: "Comprehensive collection and processing of iron and steel scrap materials.",
      link: "products",
      section: "ferrous-materials"
    },
    {
      icon: <Truck className="w-8 h-8 text-[#ffc533]" />,
      title: "Non-Ferrous Materials",
      description: "Specialized handling of aluminum, copper, brass, and other non-ferrous metals.",
      link: "products",
      section: "non-ferrous-materials"
    },
    {
      icon: <Package className="w-8 h-8 text-[#ff41a5]" />,
      title: "Rubber Materials",
      description: "Complete range of recycled rubber products from end-of-life tyres and industrial rubber.",
      link: "products",
      section: "rubber-materials"
    },
    {
      icon: <Globe className="w-8 h-8 text-[#fe2b32]" />,
      title: "Global Trading",
      description: "International scrap trading with worldwide shipping capabilities.",
      link: "services"
    },
    {
      icon: <Award className="w-8 h-8 text-[#ffc533]" />,
      title: "Quality Assurance",
      description: "Rigorous quality control and certification for all materials.",
      link: "services"
    },
    {
      icon: <ClipboardCheck className="w-8 h-8 text-[#ff41a5]" />,
      title: "Quality Inspection",
      description: "Ensuring Rigorous quality control and certification complying International Standards.",
      link: "services"
    }
  ];

  const stats = [
    { number: "4+", label: "Years of Excellence" },
    { number: "500+", label: "Satisfied Clients" },
    { number: "50+", label: "Countries Served" },
    { number: "24/7", label: "Customer Support" }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative animated-gradient text-white py-32 md:py-40 lg:py-48 overflow-hidden">
        {/* Aurora Effect */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-r from-[#fe2b32]/30 to-[#ff41a5]/30 rounded-full aurora-float aurora-glow"></div>
          <div className="absolute top-20 right-1/4 w-80 h-80 bg-gradient-to-r from-[#ff41a5]/25 to-[#ffc533]/25 rounded-full aurora-float aurora-glow" style={{animationDelay: '2s'}}></div>
          <div className="absolute bottom-10 left-1/3 w-72 h-72 bg-gradient-to-r from-[#ffc533]/20 to-[#fe2b32]/20 rounded-full aurora-float aurora-glow" style={{animationDelay: '4s'}}></div>
          <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-gradient-to-r from-[#ff41a5]/15 to-[#ffc533]/15 rounded-full aurora-float aurora-glow" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-1/3 left-1/6 w-56 h-56 bg-gradient-to-r from-[#ffc533]/20 to-[#fe2b32]/20 rounded-full aurora-float aurora-glow" style={{animationDelay: '3s'}}></div>
          <div className="absolute bottom-1/4 right-1/6 w-48 h-48 bg-gradient-to-r from-[#fe2b32]/15 to-[#ff41a5]/15 rounded-full aurora-float aurora-glow" style={{animationDelay: '5s'}}></div>
        </div>
        
        {/* Animated gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-black/20 to-black/40"></div>
        
        <div className="relative container mx-auto px-4 text-center">
          <div className="flex justify-center mb-8">
            <ImageWithFallback 
              src="/assets/icons/logo-w.svg"
              alt="Double Click Trading Logo" 
              className="h-[110px] md:h-[147px] w-auto"
            />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Shaping the Future of Scrap Trade
          </h1>
          <p className="text-xl mb-8 max-w-3xl mx-auto text-gray-200">
            Trusted since 2020, DOUBLE CLICK TRADING FZE is your global partner for reliable scrap trading solutions
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-[#fe2b32] hover:bg-red-600"
              onClick={() => onPageChange('products')}
            >
              View Our Products
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-[#ffc533] text-[#ffc533] hover:bg-[#ffc533] hover:text-black"
              onClick={() => onPageChange('contact')}
            >
              Get Quote
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-[#fe2b32] mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Core Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Delivering comprehensive scrap trading solutions with global reach and deep local expertise.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card 
                key={index} 
                className="hover:shadow-lg cursor-pointer hover:scale-105 transform transition-all duration-300"
                onClick={() => {
                  onPageChange(service.link);
                  // Scroll to specific section after page change
                  if (service.section) {
                    setTimeout(() => {
                      const element = document.getElementById(service.section);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                      }
                    }, 100);
                  }
                }}
              >
                <CardContent className="p-6 text-center">
                  <div className="mb-4 flex justify-center">{service.icon}</div>
                  <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                  <div className="mt-4 text-sm text-[#fe2b32] font-medium">
                    Learn More →
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Why Choose Double Click Trading?</h2>
              <p className="text-lg text-gray-600 mb-6">
                Based in the heart of Dubai's thriving business district, we combine local market 
                knowledge with global trading expertise to deliver exceptional value to our clients.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#fe2b32] rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span>Established in 2020 with a rapid growth trajectory, demonstrating strong market acceptance and business momentum.</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#ffc533] rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span>Combines extensive market expertise with a global trading perspective to deliver tailored and effective solutions.</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#ff41a5] rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span>Maintains comprehensive quality control and certification standards, ensuring reliability and trustworthiness.</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#fe2b32] rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span>Offers competitive pricing to provide cost-effective trading services.</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#ffc533] rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span>Provides flexible payment terms for client convenience and financial adaptability.</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-[#ff41a5] rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <span>Committed to delivering exceptional value through client-focused operational excellence.</span>
                </li>
              </ul>
              <Button 
                size="lg" 
                className="bg-[#fe2b32] hover:bg-red-600"
                onClick={() => onPageChange('about')}
              >
                Learn More About Us
              </Button>
            </div>
            <div className="relative">
              <ImageWithFallback 
                src="/assets/images/why choose double click.jpg"
                alt="Why Choose Double Click Trading" 
                className="rounded-lg shadow-lg w-full h-96 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#fe2b32] to-[#ff41a5] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Start Trading?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Get in touch with our experts today for competitive quotes and reliable scrap trading solutions.
          </p>
          <Button 
            size="lg" 
            variant="secondary"
            className="bg-white text-[#fe2b32] hover:bg-gray-100"
            onClick={() => onPageChange('contact')}
          >
            Contact Us Today
          </Button>
        </div>
      </section>
    </div>
  );
}